const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:4000';
const MAX_RETRIES = 3;
const RETRY_DELAYS = [1000, 2000, 4000];
function isRetryable(status, method) {
    if (method !== 'GET' && method !== 'HEAD')
        return false;
    return status === 0 || status === 502 || status === 503 || status === 504 || status >= 520;
}
async function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}
async function fetchAPI(endpoint, options = {}) {
    const { body, ...restOptions } = options;
    const method = (restOptions.method || 'GET').toUpperCase();
    const headers = {
        'Content-Type': 'application/json',
        ...restOptions.headers,
    };
    const token = localStorage.getItem('auth_token');
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    let lastError = null;
    for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        try {
            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                ...restOptions,
                headers,
                credentials: 'include',
                body: body ? JSON.stringify(body) : undefined,
            });
            if (!response.ok) {
                if (attempt < MAX_RETRIES && isRetryable(response.status, method)) {
                    console.warn(`[API] Retrying ${endpoint} (attempt ${attempt + 1}/${MAX_RETRIES}) — status ${response.status}`);
                    await sleep(RETRY_DELAYS[attempt]);
                    continue;
                }
                const error = await response.json().catch(() => ({ error: 'Request failed' }));
                throw new Error(error.error || `Request failed: ${response.status} ${response.statusText}`);
            }
            return response.json();
        }
        catch (err) {
            lastError = err instanceof Error ? err : new Error('Request failed');
            const isNetworkError = lastError.name === 'TypeError' ||
                lastError.message.includes('NetworkError') ||
                lastError.message.includes('Failed to fetch');
            if (attempt < MAX_RETRIES && (isNetworkError || (method === 'GET' && !lastError.message.includes('Request failed:')))) {
                console.warn(`[API] Retrying ${endpoint} (attempt ${attempt + 1}/${MAX_RETRIES}) — ${lastError.message}`);
                await sleep(RETRY_DELAYS[attempt]);
                continue;
            }
            throw lastError;
        }
    }
    throw lastError || new Error('Request failed after retries');
}
// Auth API
export const authAPI = {
    login: (email, password) => fetchAPI('/auth/login', {
        method: 'POST',
        body: { email, password },
    }),
    logout: () => fetchAPI('/auth/logout', {
        method: 'POST',
    }),
    me: () => fetchAPI('/auth/me'),
};
// Products API
export const productsAPI = {
    getAll: (params) => {
        const query = new URLSearchParams();
        if (params?.category)
            query.append('category', params.category);
        if (params?.search)
            query.append('search', params.search);
        const queryString = query.toString();
        return fetchAPI(`/products${queryString ? `?${queryString}` : ''}`);
    },
    getBySlug: (slug) => fetchAPI(`/products/${slug}`),
    create: (data) => fetchAPI('/products', {
        method: 'POST',
        body: data,
    }),
    update: (id, data) => fetchAPI(`/products/${id}`, {
        method: 'PUT',
        body: data,
    }),
    delete: (id) => fetchAPI(`/products/${id}`, {
        method: 'DELETE',
    }),
};
// Orders API
export const ordersAPI = {
    create: (data) => fetchAPI('/orders', {
        method: 'POST',
        body: data,
    }),
    getAll: (params) => {
        const query = new URLSearchParams();
        if (params?.status)
            query.append('status', params.status);
        if (params?.search)
            query.append('search', params.search);
        const queryString = query.toString();
        return fetchAPI(`/orders${queryString ? `?${queryString}` : ''}`);
    },
    getById: (id) => fetchAPI(`/orders/${id}`),
    getConfirmation: (id) => fetchAPI(`/orders/${id}/confirmation`),
    updateStatus: (id, status, paymentStatus) => {
        const body = { status };
        if (paymentStatus)
            body.paymentStatus = paymentStatus;
        return fetchAPI(`/orders/${id}/status`, {
            method: 'PATCH',
            body,
        });
    },
};
// Packages API
export const packagesAPI = {
    getAll: () => fetchAPI('/packages'),
    getById: (id) => fetchAPI(`/packages/${id}`),
    create: (data) => fetchAPI('/packages', {
        method: 'POST',
        body: data,
    }),
    update: (id, data) => fetchAPI(`/packages/${id}`, {
        method: 'PUT',
        body: data,
    }),
    delete: (id) => fetchAPI(`/packages/${id}`, {
        method: 'DELETE',
    }),
};
// Discounts API
export const discountsAPI = {
    getAll: () => fetchAPI('/discounts'),
    create: (data) => fetchAPI('/discounts', {
        method: 'POST',
        body: data,
    }),
    update: (id, data) => fetchAPI(`/discounts/${id}`, {
        method: 'PUT',
        body: data,
    }),
    delete: (id) => fetchAPI(`/discounts/${id}`, {
        method: 'DELETE',
    }),
    validate: (code, subtotalCents) => fetchAPI('/discounts/validate', {
        method: 'POST',
        body: { code, subtotalCents },
    }),
};
// Payments API
export const paymentsAPI = {
    createPreference: (orderId) => fetchAPI('/payments/create-preference', {
        method: 'POST',
        body: { orderId },
    }),
    getStatus: (paymentId) => fetchAPI(`/payments/status/${paymentId}`),
};
